import DialogContent from '@mui/material/DialogContent'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import { DataEditContextTextField } from 'Components/reusable/DataContext/fields/DataEditContextTextField'
import { DataEditContextDateField } from 'Components/reusable/DataContext/fields/DataEditContextDateField'
import { DataEditContextDateResolutionField } from 'Components/reusable/DataContext/fields/DataEditContextDateResolutionField'
import { DataEditContextCertificateField } from 'Components/reusable/DataContext/fields/DataEditContextCertificateField'
import { DataEditContextCompetenceField } from 'Components/reusable/DataContext/fields/DataEditContextCompetenceField'
import { DataEditContextTranslationImport } from 'Components/reusable/DataContext/TranslationImport'
import Grid from '@mui/material/Grid2'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  IPersonCertificate,
  ICourse,
  IEducation,
  IPersonCompetence,
  ICertificate,
  ICompetence,
} from 'types/cvsInterfaces'
import newWithEditModal from 'Components/reusable/HOC/newWithEditModal'
import { NewOrExisting } from 'Components/reusable/DataContext'
import { useUser } from 'hooks'
import { cvCompetencesAPI, cvCertificatesAPI, cvCoursesAPI, cvEducationsAPI, CvSubAPI } from 'api/CvAPI'
import { ObjectSchema } from 'yup'
import {
  getCertificatesInitialData,
  getCompetencesInitialData,
  getCourseInitialData,
  getEducationInitialData,
} from 'Components/reusable/DataContext/InitialData'
import {
  certificateSchema,
  competenceSchema,
  courseSchema,
  educationSchema,
} from 'Components/reusable/DataContext/ValidationSchema'

/** @notExported */
interface EducationModalProps {
  /** Education item. */
  item?: IEducation | ICourse | IPersonCertificate | IPersonCompetence | null
  /** Data object. */
  data: IEducation | ICourse | IPersonCertificate | IPersonCompetence
  /** CV ID. */
  cvId: string
  /** Set type function. */
  setType: (type: string) => void
  /** Set locale base function. */
  setLocaleBase: (localeBase: string) => void
  /** Set schema function. */
  setSchema: (schema: ObjectSchema) => void
  /** Set API function. */
  setApi: (
    api: CvSubAPI<IPersonCompetence> | CvSubAPI<IPersonCertificate> | CvSubAPI<IEducation> | CvSubAPI<ICourse>
  ) => void
  /** Set data function. */
  setData: (
    data:
      | NewOrExisting<IEducation>
      | NewOrExisting<ICourse>
      | NewOrExisting<IPersonCertificate>
      | NewOrExisting<IPersonCompetence>
  ) => void
  /** Used certificates. */
  usedCertificates?: IPersonCertificate[]
  /** Used competences. */
  usedCompetences?: IPersonCompetence[]
  /** The form was restored from a CV wizard draft (set by `newWithEditModal`). */
  restoredFromDraft?: boolean
}

/**
 * Tells which education type the form data is.
 *
 * @param data - Form data.
 * @param competencesEnabled - The user has the `competences` feature.
 * @returns Education type, or undefined if it can't be told.
 */
const getEducationType = (
  data: IEducation | ICourse | IPersonCertificate | IPersonCompetence,
  competencesEnabled: boolean
): string | undefined => {
  if (data.translations.length && 'degree' in data.translations[0]) return 'degrees'
  if (data.translations.length && 'course' in data.translations[0]) return 'courses'
  if ('Certificate' in data) return 'certificates'
  if ('Competence' in data && competencesEnabled) return 'competences'
  return undefined
}

/**
 * Education modal.
 *
 * @returns Education modal component.
 * @notExported
 */
const EducationModal: React.FC<EducationModalProps> = ({
  item,
  data,
  cvId,
  setType,
  setLocaleBase,
  setSchema,
  setApi,
  setData,
  usedCertificates,
  usedCompetences,
  restoredFromDraft,
}) => {
  const { t } = useTranslation()
  const { features } = useUser()
  // A restored draft of a new item keeps its data: start on its type and don't reset the form to that type's
  // defaults. The reset is skipped while the type is still the restored one (effects can run twice in StrictMode),
  // and works again once the user switches the type.
  const restoredTypeRef = useRef<string | undefined>(
    restoredFromDraft && !item ? getEducationType(data, features.includes('competences')) : undefined
  )
  const [educationType, setEducationType] = useState<string>(restoredTypeRef.current ?? 'degrees')

  useEffect(() => {
    if (educationType) {
      setType(educationType)
      const keepData = restoredTypeRef.current === educationType
      if (!keepData) restoredTypeRef.current = undefined
      ;(async () => {
        if (educationType === 'competences') {
          setLocaleBase('profile.competence.modal')
          setSchema(competenceSchema())
          setApi(cvCompetencesAPI)
          if (!item && !keepData) {
            setData(await getCompetencesInitialData(parseInt(cvId)))
          }
        } else if (educationType === 'certificates') {
          setLocaleBase('profile.certificate.modal')
          setSchema(certificateSchema())
          setApi(cvCertificatesAPI)
          if (!item && !keepData) {
            setData(await getCertificatesInitialData(parseInt(cvId)))
          }
        } else if (educationType === 'courses') {
          setLocaleBase('profile.course.modal')
          setSchema(courseSchema())
          setApi(cvCoursesAPI)
          if (!item && !keepData) {
            setData(await getCourseInitialData(parseInt(cvId)))
          }
        } else {
          setLocaleBase('profile.education.modal')
          setSchema(educationSchema())
          setApi(cvEducationsAPI)
          if (!item && !keepData) {
            setData(await getEducationInitialData(parseInt(cvId)))
          }
        }
      })()
    }
  }, [educationType, setType, setLocaleBase, setSchema, setApi, setData, cvId, item])

  useEffect(() => {
    if (item) {
      const itemType = getEducationType(data, features.includes('competences'))
      if (itemType) setEducationType(itemType)
    }
  }, [data, data.translations, features, item])

  const getDisabledOptions = useCallback(() => {
    const disabledOptions: (ICertificate | ICompetence)[] = []
    if (educationType === 'certificates') {
      usedCertificates
        ?.filter(cert => cert.id !== item?.id)
        .map(item => {
          if (item.Certificate) disabledOptions.push(item.Certificate)
        })
    } else if (educationType === 'competence') {
      usedCompetences
        ?.filter(comp => comp.id !== item?.id)
        .map(item => {
          if (item.Competence) disabledOptions.push(item.Competence)
        })
    }

    return disabledOptions
  }, [educationType, item?.id, usedCertificates, usedCompetences])

  // item-specific fields
  const fields: React.ReactNode = useMemo(() => {
    if (educationType === 'competences') {
      return (
        <>
          <DataEditContextCompetenceField<IPersonCompetence>
            field="competenceId"
            dataField="Competence"
            required
            fullWidth
            usedSkills={getDisabledOptions()}
            insetLabel
            disabled={item && item.id}
          />
          <DataEditContextTextField<IPersonCompetence> field="issuer" required fullWidth insetLabel />
        </>
      )
    } else if (educationType === 'certificates') {
      return (
        <>
          <DataEditContextCertificateField<IPersonCertificate>
            field="certificateId"
            dataField="Certificate"
            required
            fullWidth
            usedSkills={getDisabledOptions()}
            insetLabel
            disabled={item && item.id}
          />
          <DataEditContextTextField<IPersonCertificate> field="certifier" required fullWidth insetLabel />
          <DataEditContextTextField<IPersonCertificate> field="licenceNumber" fullWidth insetLabel />
          <DataEditContextTextField<IPersonCertificate> field="url" fullWidth insetLabel />
        </>
      )
    } else if (educationType === 'courses') {
      return (
        <>
          <DataEditContextTextField<ICourse> field="course" required fullWidth insetLabel />
          <DataEditContextTextField<ICourse> field="organizer" required fullWidth insetLabel />
        </>
      )
    } else {
      return (
        <>
          <DataEditContextTextField<IEducation> field="degree" required fullWidth insetLabel />
          <DataEditContextTextField<IEducation> field="school" required fullWidth insetLabel />
        </>
      )
    }
  }, [educationType, getDisabledOptions, item])

  const dataStartDate = 'startDate' in data ? data.startDate : undefined
  const dataIssueDate = 'issueDate' in data ? data.issueDate : undefined

  const dateFields: React.ReactNode = useMemo(() => {
    if (educationType === 'competences') {
      return (
        <>
          <Grid size={6}>
            <DataEditContextDateResolutionField<IPersonCompetence> field="dateResolution" required />
          </Grid>
          <Grid size={6}>
            <DataEditContextDateField<IPersonCompetence> field="issueDate" required start={true} insetLabel />
            <DataEditContextDateField<IPersonCompetence>
              field="endDate"
              isClearable
              end={true}
              minDate={dataIssueDate}
              insetLabel
            />
          </Grid>
        </>
      )
    } else if (educationType === 'certificates') {
      return (
        <>
          <Grid size={6}>
            <DataEditContextDateResolutionField<IPersonCertificate> field="dateResolution" required />
          </Grid>
          <Grid size={6}>
            <DataEditContextDateField<IPersonCertificate> field="issueDate" required insetLabel />
            <DataEditContextDateField<IPersonCertificate>
              field="endDate"
              isClearable
              end={true}
              minDate={dataIssueDate}
              insetLabel
            />
          </Grid>
        </>
      )
    } else if (educationType === 'courses') {
      return (
        <>
          <Grid size={6}>
            <DataEditContextDateResolutionField<ICourse> field="dateResolution" required />
          </Grid>
          <Grid size={6}>
            <DataEditContextDateField<ICourse> field="startDate" required start={true} insetLabel />
            <DataEditContextDateField<ICourse>
              field="endDate"
              isClearable
              end={true}
              minDate={dataStartDate}
              insetLabel
            />
          </Grid>
        </>
      )
    } else {
      return (
        <>
          <Grid size={6}>
            <DataEditContextDateResolutionField<IEducation> field="dateResolution" required />
          </Grid>
          <Grid size={6}>
            <DataEditContextDateField<IEducation> field="startDate" required start={true} insetLabel />
            <DataEditContextDateField<IEducation>
              field="endDate"
              isClearable
              end={true}
              minDate={dataStartDate}
              insetLabel
            />
          </Grid>
        </>
      )
    }
  }, [dataIssueDate, dataStartDate, educationType])

  return (
    <DialogContent>
      <Grid container spacing={2} direction="row">
        <Grid size={6}>
          <Grid container spacing={2} direction="column">
            {item ? null : (
              <Grid>
                <FormControl component="fieldset">
                  <RadioGroup
                    aria-label="educationType"
                    name="educationType"
                    value={educationType}
                    onChange={e => setEducationType(e.target.value)}
                  >
                    <FormControlLabel value="degrees" control={<Radio />} label={t('profile.education.modal.degree')} />
                    <FormControlLabel value="courses" control={<Radio />} label={t('profile.course.modal.course')} />
                    <FormControlLabel
                      value="certificates"
                      control={<Radio />}
                      label={t('profile.certificate.modal.certificate')}
                    />
                    {features.includes('competences') && (
                      <FormControlLabel
                        value="competences"
                        control={<Radio />}
                        label={t('profile.competence.modal.competence')}
                      />
                    )}
                  </RadioGroup>
                </FormControl>
              </Grid>
            )}
            <Grid>
              {data.translations && (
                <DataEditContextTranslationImport<IPersonCompetence | IPersonCertificate | ICourse | IEducation> />
              )}
              {fields}
              <Grid container alignItems="center" direction="row">
                {dateFields}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid size={6}>
          <DataEditContextTextField<IEducation | ICourse | IPersonCertificate | IPersonCompetence>
            field="description"
            maxHintLength={1000}
            multiline
            grow
            rows={21}
            fullWidth
            insetLabel
          />
        </Grid>
      </Grid>
    </DialogContent>
  )
}

export default newWithEditModal(EducationModal)
